import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';
import { toDistanceFromNowISOStrict } from 'utils/DateHelper';

import Ratio from './RatiosModel';
import Insight from './InsightModel';
import Manager from './ManagerModel';
import BankAccountsPreview from './BankAccountsPreviewModel';
import CreditLinesPreview from './CreditLinesPreviewModel';
import SnapshotIntegrations from './SnapshotIntegrationsModel';
import BalanceSheetPreview from './BalanceSheetPreviewModel';

const SnapshotPreviewRecord = Record({
  bankAccountCreditLines: null,
  providerCreditLines: null,
  integrations: null,
  generatedAt: null,
  lastFiledAccountsDate: null,
  annualRevenue: null,
  creditScoreGrade: '0',
  creditScore: '0',
  creditScoreGradePrevious: '0',
  creditScorePrevious: '0',
  latestRatingChangeDate: null,
  entitySector: '23',
  creditScoreText: '',
  creditLimit: null,
  managedBy: null,
  ratios: null,
  financialInsight: null,
  financialInsightTags: [],
  balanceSheet: null,
  annualProfitNet: null,
  sector: null,
  tradeHistory: [],
  profitLoss: null,
  registrationDate: null,
  financialYearEndMonth: null,
  insightsCount: null,
  insightsNames: [],
  isCompanyDissolved: null,
  lastTransactionDate: null,
  riskFactors: null,
  paymentPerformance: null,
  numberOfActiveCcjs: null,
});

export default class SnapshotPreview extends SnapshotPreviewRecord {
  static deserialize(jsonObject) {
    const mapProperties = {
      lastUpdated: Hydrator.timestamp2Date,
      bankAccountCreditLines: BankAccountsPreview.deserialize,
      providerCreditLines: CreditLinesPreview.deserialize,
      integrations: SnapshotIntegrations.deserialize,
      managedBy: Manager.deserialize,
      financialInsight: Insight.deserialize,
      ratios: Ratio.deserialize,
      balanceSheet: BalanceSheetPreview.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new SnapshotPreview(hydrated);
  }

  get repaid() {
    return this.hasCreditLinesData && this.providerCreditLines.repaid;
  }

  get percentRepaid() {
    return this.hasCreditLinesData && this.providerCreditLines.percentRepaid;
  }

  get isSuccessful() {
    return this.hasCreditLinesData && this.providerCreditLines.isSuccessful;
  }

  get providerCreditLinesAmount() {
    return this.hasCreditLinesData && this.providerCreditLines.amount;
  }

  get providerCreditLinesBalance() {
    return this.hasCreditLinesData && this.providerCreditLines.balance;
  }

  get providerCreditLinesExpiry() {
    return this.hasCreditLinesData && this.providerCreditLines.expiry;
  }

  get providerCreditLinesExpiryDate() {
    return this.hasCreditLinesData && this.providerCreditLines.endDate;
  }

  get bankAccountStatementBalance() {
    return this.hasBankAccountData ? this.bankAccountCreditLines.balance : 0;
  }

  get hasBankAccountData() {
    return !!this.bankAccountCreditLines;
  }

  get hasCreditLinesData() {
    return !!this.providerCreditLines;
  }

  get lastUpdatedFromNow() {
    return this.generatedAt ? `${toDistanceFromNowISOStrict(this.generatedAt)} ago` : '-';
  }

  get registrationDate() {
    return this.get('registrationDate') ? new Date(this.get('registrationDate')) : null;
  }

  get negativeRiskFactorsCount() {
    return this.riskFactors?.counters?.find(({ weight }) => weight === 'NEGATIVE')?.count;
  }
}
